import { api, ApiError } from '@/api';
import type { PublicConfig } from '@/api/types';

export type TenantLoadResult =
  | { kind: 'ready'; config: PublicConfig }
  | { kind: 'redirect'; to: string }
  | { kind: 'not-found' }
  | { kind: 'inactive'; name: string; status: 'suspended' | 'archived' }
  | { kind: 'unavailable'; error: unknown };

/**
 * First request of every page load: which space does THIS hostname serve, and
 * how should it look? The API resolves the tenant from the Host header; the
 * browser never tells it which tenant it is.
 */
export async function loadTenantConfig(): Promise<TenantLoadResult> {
  try {
    const config = await api.get<PublicConfig>('/public/tenant', { auth: false });
    if (config.redirectTo) {
      const { pathname, search, hash } = window.location;
      return { kind: 'redirect', to: `${config.redirectTo}${pathname}${search}${hash}` };
    }
    // Sign-in is refused for a suspended or archived space: say so up front
    // instead of showing a login that can only fail.
    if (config.tenant && config.tenant.status !== 'active') {
      return { kind: 'inactive', name: config.tenant.displayName, status: config.tenant.status };
    }
    return { kind: 'ready', config };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      // An unknown address under the platform domain: go to the brand site.
      const landing = landingUrl(error.data);
      return landing ? { kind: 'redirect', to: landing } : { kind: 'not-found' };
    }
    return { kind: 'unavailable', error };
  }
}

function landingUrl(body: unknown): string | null {
  const details = (body as { details?: { landingUrl?: unknown } } | undefined)?.details;
  const url = details?.landingUrl;
  return typeof url === 'string' && /^https?:\/\//.test(url) ? url : null;
}
